import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const ComoUsarIAApostasPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const passosUsar = [
    {
      passo: '1',
      titulo: 'Acesse o Chat da IA',
      descricao: 'Entre no chat do PickStar AI (ou este chat) onde apostas esportivas são nossa especialidade'
    },
    {
      passo: '2',
      titulo: 'Faça Perguntas Específicas sobre Apostas',
      descricao: 'Exemplo: "Quais são as odds dos jogos da NBA hoje?" ou "Me dá um palpite para Liverpool x Man City?"'
    },
    {
      passo: '3',
      titulo: 'Analise a Resposta da IA',
      descricao: 'Receba respostas detalhadas com estatísticas, odds em tempo real, níveis de confiança e razões por trás dos palpites específicos'
    },
    {
      passo: '4',
      titulo: 'Aposte com Mais Confiança',
      descricao: 'Use os insights da IA junto com sua própria pesquisa para fazer apostas mais inteligentes e informadas'
    }
  ];

  const tiposPerguntas = [
    {
      categoria: '1. Palpites e Análise de Jogos',
      exemplos: [
        '"Me dê uma pesquisa sobre o palpite vencedor para Flamengo vs Palmeiras"',
        '"Detalhe o confronto Corinthians vs Santos — odds, estatísticas e a vantagem"',
        '"Quem é realmente o favorito em Atlético-MG vs Grêmio — e por quê?"'
      ]
    },
    {
      categoria: '2. Estatísticas Profundas e Histórico',
      exemplos: [
        '"Me mostre o histórico de confrontos diretos entre Palmeiras e São Paulo — quem domina?"',
        '"Qual a força do Flamengo em casa nesta temporada — só os números"',
        '"Qual o desempenho do Grêmio nos últimos 5 jogos — fatos concretos"'
      ]
    },
    {
      categoria: '3. Mercados Específicos de Apostas',
      exemplos: [
        '"Projete o total de escanteios para o jogo de hoje"',
        '"Me dê as tendências de cartões em jogos de rivalidade"',
        '"Ambos marcam em Internacional vs Athletico-PR — sim ou não, com dados"'
      ]
    },
    {
      categoria: '4. Estratégia Inteligente e Controle de Banca',
      exemplos: [
        '"Monte uma múltipla segura para o Brasileirão hoje"',
        '"Melhores dicas para montar um bilhete vencedor"'
      ]
    },
    {
      categoria: '5. Modelos, Análises e Dados ao Vivo',
      exemplos: [
        '"Construa recomendações de props de jogadores com modelo"',
        '"Qual sua confiança nas previsões de estatísticas de jogadores hoje? E por quê?"',
        '"Combine relatórios de lesões, estatísticas ao vivo e notícias de última hora em um palpite — agora"'
      ]
    }
  ];

  const faqs = [
    {
      q: "Posso confiar 100% na IA?",
      a: "Nenhuma previsão é garantida. Nossa IA é refinada para pensar logicamente com números e modelos estatísticos, te guiando da forma que você deseja ser guiado e personalizada para sua estratégia. Fizemos backtesting interno em mais de 4.000 partidas de futebol e temos uma taxa de acerto de 78-81% em apostas diretas em picks de alta confiança. Sempre gerencie sua banca e nunca aposte mais do que pode perder."
    },
    {
      q: "Quanto custa?",
      a: "A versão gratuita oferece picks diários e uso diário do PickStar AI. Usamos StarCoins como nossa moeda virtual. Agora você pode começar com apenas R$2,00 e ir até R$275. Nunca expiram e você pode usar StarCoins para todos os recursos do app."
    }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Como Usar IA para Apostas Esportivas",
    "description": "Guia completo para usar inteligência artificial nas suas apostas esportivas com o PickStar AI. Aprenda a fazer perguntas certas e maximizar seus lucros.",
    "step": passosUsar.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.titulo,
      "text": step.descricao
    })),
    "totalTime": "PT5M"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Como Usar IA para Apostas Esportivas | Guia Completo PickStar AI"
        description="Aprenda a usar inteligência artificial para apostas esportivas. Guia completo do PickStar AI com exemplos práticos, dicas profissionais e estratégias vencedoras."
        keywords="como usar ia para apostas, inteligência artificial apostas esportivas, ia apostas futebol, como apostar com ia, pickstar ai tutorial, apostas com inteligência artificial"
        canonical="https://pickstarai.com/como-usar-ia-apostas-esportivas"
        ogImage="https://pickstarai.com/og-home-logo.png"
        structuredData={[structuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                Como Usar IA para Apostas
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                Guia completo do básico às estratégias avançadas
              </p>
            </motion.div>
            <div className="mt-8">
              <button
                onClick={() => navigate('/chat')}
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                         text-[11px] font-medium text-white uppercase tracking-[0.15em]
                         hover:from-emerald-500 hover:to-violet-500 transition-all"
              >
                COMEÇAR GRÁTIS
                <ChevronRight className="ml-2 w-3 h-3" />
              </button>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Passo a Passo */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">GUIA</span>
              COMO COMEÇAR EM 4 PASSOS
            </h2>

            <div className="space-y-4">
              {passosUsar.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 border border-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400/80 font-mono text-sm">{step.passo}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm text-white/80 uppercase tracking-[0.15em] font-medium mb-3">
                        {step.titulo}
                      </h3>
                      <p className="text-sm text-white/50">{step.descricao}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Tipos de Perguntas */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">EXEMPLOS</span>
              O QUE PERGUNTAR PARA A IA
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {tiposPerguntas.map((tipo, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <h3 className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-4">
                    {tipo.categoria}
                  </h3>
                  <ul className="space-y-2">
                    {tipo.exemplos.map((exemplo, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-1 h-1 bg-emerald-500/50 mt-2" />
                        <span className="text-sm text-white/50">{exemplo}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.q}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              PRONTO PARA USAR IA?
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Junte-se a milhares de apostadores que já usam IA para lucrar mais e apostar com inteligência.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              COMEÇAR A USAR IA GRÁTIS
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default ComoUsarIAApostasPage;
